import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { FieldHint } from '@/components/shared/FieldHint';
import { useCreatePolicy, useUpdatePolicy } from '@/hooks/usePolicies';
import { parseResourceSelectors, selectorsToInput, formatSelectorLabel, SELECTOR_FIELDS } from '@/lib/policySelectors';
import type { Policy, PolicyType, PolicyEffect, PolicyCondition, PolicyRule } from '@/types/policy';


const OPERATORS: { value: PolicyCondition['operator']; label: string }[] = [
  { value: 'eq', label: 'equals' },
  { value: 'ne', label: 'not equals' },
  { value: 'in', label: 'in' },
  { value: 'contains', label: 'contains' },
  { value: 'gte', label: '>=' },
  { value: 'lte', label: '<=' },
];

interface PolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialData?: Policy;
}

function emptyCondition(): PolicyCondition {
  return { field: '', operator: 'eq', value: '' };
}

function emptyRule(): PolicyRule {
  return {
    id: `rule_${Date.now()}`,
    description: '',
    effect: 'allow',
    conditions: [emptyCondition()],
    priority: 1,
  };
}

export function PolicyDialog({ open, onOpenChange, mode, initialData }: PolicyDialogProps) {
  const createMutation = useCreatePolicy();
  const updateMutation = useUpdatePolicy();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [type, setType] = React.useState<PolicyType>('rbac');
  const [effect, setEffect] = React.useState<PolicyEffect>('allow');
  const [rules, setRules] = React.useState<PolicyRule[]>([emptyRule()]);
  const [resourceSelectors, setResourceSelectors] = React.useState('');
  const [applyToAllResources, setApplyToAllResources] = React.useState(false);
  const [error, setError] = React.useState('');
  const selectorPreview = React.useMemo(() => parseResourceSelectors(resourceSelectors), [resourceSelectors]);

  React.useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialData) {
        setName(initialData.name);
        setDescription(initialData.description);
        setType(initialData.type);
        setEffect(initialData.effect);
        setRules(initialData.rules.length > 0 ? initialData.rules : [emptyRule()]);
        setResourceSelectors(selectorsToInput(initialData.resource_selectors));
        setApplyToAllResources(false);
      } else {
        setName('');
        setDescription('');
        setType('rbac');
        setEffect('allow');
        setRules([emptyRule()]);
        setResourceSelectors('');
        setApplyToAllResources(false);
      }
      setError('');
    }
  }, [open, mode, initialData]);

  function updateRule(index: number, updates: Partial<PolicyRule>) {
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...updates } : r)));
  }

  function updateCondition(ruleIndex: number, condIndex: number, updates: Partial<PolicyCondition>) {
    setRules((prev) =>
      prev.map((r, ri) =>
        ri === ruleIndex
          ? { ...r, conditions: r.conditions.map((c, ci) => (ci === condIndex ? { ...c, ...updates } : c)) }
          : r
      )
    );
  }

  function addCondition(ruleIndex: number) {
    setRules((prev) =>
      prev.map((r, i) => (i === ruleIndex ? { ...r, conditions: [...r.conditions, emptyCondition()] } : r))
    );
  }

  function removeCondition(ruleIndex: number, condIndex: number) {
    setRules((prev) =>
      prev.map((r, i) =>
        i === ruleIndex ? { ...r, conditions: r.conditions.filter((_, ci) => ci !== condIndex) } : r
      )
    );
  }

  function addRule() {
    setRules((prev) => [...prev, { ...emptyRule(), id: `rule_${Date.now()}`, priority: prev.length + 1 }]);
  }

  function removeRule(index: number) {
    setRules((prev) => prev.filter((_, i) => i !== index));
  }

  const VALID_PREFIXES = ['resource.', 'principal.', 'relation.'];

  function validate(): string | null {
    if (!name.trim()) return 'Policy name is required';
    if (!description.trim()) return 'Description is required';
    if (rules.length === 0) return 'At least one rule is required';
    for (let i = 0; i < rules.length; i++) {
      const rule = rules[i];
      if (!rule.description.trim()) return `Rule ${i + 1}: description is required`;
      if (rule.conditions.length === 0) return `Rule ${i + 1}: at least one condition is required`;
      for (let j = 0; j < rule.conditions.length; j++) {
        const cond = rule.conditions[j];
        if (!cond.field.trim()) return `Rule ${i + 1}, Condition ${j + 1}: field is required`;
        if (!VALID_PREFIXES.some((p) => cond.field.startsWith(p))) {
          return (
            `Rule ${i + 1}, Condition ${j + 1}: field '${cond.field}' must start with ` +
            `'resource.', 'principal.', or 'relation.'. ` +
            `Bare field names silently resolve against the principal — ` +
            `use 'resource.${cond.field}' to check a resource attribute.`
          );
        }
        if (cond.value === '' || (Array.isArray(cond.value) && cond.value.length === 0)) {
          return `Rule ${i + 1}, Condition ${j + 1}: value is required`;
        }
      }
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');

    // key:value pairs (domain:finance) or JSON objects, parsed into exactly the
    // objects the API stores. The old fallback stored the raw token as both
    // field and value, a selector that matches nothing (cold run 2026-09-25, P0).
    const parsedSelectors = parseResourceSelectors(resourceSelectors);
    if (parsedSelectors.error) {
      setError(`Resource Selectors: ${parsedSelectors.error}`);
      return;
    }
    const selectors = parsedSelectors.selectors;

    const processedRules = rules.map((r) => ({
      ...r,
      conditions: r.conditions.map((c) => ({
        ...c,
        value: c.operator === 'in' && typeof c.value === 'string'
          ? c.value.split(',').map((v: string) => v.trim())
          : c.value,
      })),
    }));

    const selectorsEmpty = selectors.length === 0;

    const payload = {
      name: name.trim(),
      description: description.trim(),
      type,
      effect,
      rules: processedRules,
      resource_selectors: selectors,
      ...(applyToAllResources || selectorsEmpty ? { apply_to_all_resources: true as const } : {}),
    };

    if (mode === 'edit' && initialData) {
      updateMutation.mutate(
        { id: initialData.id, data: payload },
        {
          onSuccess: () => onOpenChange(false),
          onError: (err) => setError(err instanceof Error ? err.message : 'Failed to update policy'),
        }
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => onOpenChange(false),
        onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create policy'),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Create Policy' : 'Edit Policy'}</DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Define a new access control policy with rules and conditions.'
              : 'Update policy configuration and rules.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="My Policy" required />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select value={type} onValueChange={(v) => setType(v as PolicyType)} disabled={mode === 'edit'}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="rbac">RBAC (Role-Based)</SelectItem>
                  <SelectItem value="abac">ABAC (Attribute-Based)</SelectItem>
                  <SelectItem value="rebac">ReBAC (Relationship-Based)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What does this policy do?" required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Default Effect</label>
              <Select value={effect} onValueChange={(v) => setEffect(v as PolicyEffect)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="allow">Allow</SelectItem>
                  <SelectItem value="deny">Deny</SelectItem>
                </SelectContent>
              </Select>
              <FieldHint>
                What happens when this policy&apos;s selectors match a resource but no rule below
                matches. Allow is the usual starting point. Deny here means deny unless a rule
                explicitly allows.
              </FieldHint>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Resource Selectors</label>
              <Input
                value={resourceSelectors}
                onChange={(e) => setResourceSelectors(e.target.value)}
                placeholder="domain:finance, classification:confidential"
                aria-invalid={selectorPreview.error ? true : undefined}
              />
              <FieldHint>
                Which resources this policy governs, as key:value pairs separated by commas.
                Keys: {SELECTOR_FIELDS.join(', ')}. Leave empty and tick Apply to all resources
                to cover everything, which is the right choice for your first policy.
              </FieldHint>
              {selectorPreview.error ? (
                <p className="text-xs text-destructive" data-testid="selector-error">{selectorPreview.error}</p>
              ) : selectorPreview.selectors.length > 0 ? (
                <div className="flex flex-wrap gap-1" data-testid="selector-preview">
                  {selectorPreview.selectors.map((sel, idx) => (
                    <span key={idx} className="text-xs bg-primary/10 text-primary rounded px-1.5 py-0.5 font-mono">
                      {formatSelectorLabel(sel)}
                    </span>
                  ))}
                </div>
              ) : null}
              {(!resourceSelectors.trim() || applyToAllResources) && (
                <label className="flex items-center gap-2 cursor-pointer mt-1">
                  <input
                    type="checkbox"
                    checked={applyToAllResources}
                    onChange={(e) => setApplyToAllResources(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-input accent-primary"
                  />
                  <span className="text-xs text-muted-foreground">
                    Apply to all resources (no selector filter)
                  </span>
                </label>
              )}
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Rules</h4>
              <Button type="button" variant="outline" size="sm" onClick={addRule}>
                <Plus className="h-3 w-3 mr-1" />Add Rule
              </Button>
            </div>

            {rules.map((rule, ri) => (
              <div key={rule.id} className="rounded-md border p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-2">
                    <Input
                      value={rule.description}
                      onChange={(e) => updateRule(ri, { description: e.target.value })}
                      placeholder="Rule description"
                      className="text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={rule.effect}
                      onChange={(e) => updateRule(ri, { effect: e.target.value as PolicyEffect })}
                      className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      <option value="allow">Allow</option>
                      <option value="deny">Deny</option>
                    </select>
                    {rules.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeRule(ri)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Conditions</p>
                  <FieldHint>
                    Prefix fields with resource. or principal. A bare name silently resolves
                    against the principal. principal.groups and resource.labels are lists: match
                    them with contains, not equals.
                  </FieldHint>
                  <FieldHint>
                    Use contains for list fields (principal.groups, resource.labels). Use equals
                    for single values (resource.classification).
                  </FieldHint>
                  <FieldHint>
                    The value to compare against. For in, give a comma-separated list. Do not
                    quote strings.
                  </FieldHint>
                  {rule.conditions.map((cond, ci) => (
                    <div key={ci} className="flex items-center gap-2">
                      <Input
                        value={cond.field}
                        onChange={(e) => updateCondition(ri, ci, { field: e.target.value })}
                        placeholder="resource.classification"
                        className="text-sm flex-1"
                      />
                      <select
                        value={cond.operator}
                        onChange={(e) => updateCondition(ri, ci, { operator: e.target.value as PolicyCondition['operator'] })}
                        className="h-9 rounded-md border border-input bg-background px-2 text-xs shrink-0"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <Input
                        value={Array.isArray(cond.value) ? cond.value.join(', ') : String(cond.value)}
                        onChange={(e) => updateCondition(ri, ci, { value: e.target.value })}
                        placeholder="value or val1, val2"
                        className="text-sm flex-1"
                      />
                      {rule.conditions.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCondition(ri, ci)} className="shrink-0">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" onClick={() => addCondition(ri)} className="text-xs">
                    <Plus className="h-3 w-3 mr-1" />Add Condition
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {error && (
            <p className="text-sm text-destructive bg-destructive/10 rounded-md p-2">{error}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : mode === 'create' ? 'Create Policy' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
